import React from 'react';
import { Bot, Gauge, Clock, AlertTriangle, BarChart3, TrendingUp } from 'lucide-react';

const FeaturesSection = () => {
  const features = [
    {
      icon: <Bot className="w-8 h-8 text-blue-600" />,
      title: "Automated Admin Agent",
      description: "Automatically generates and customizes reports from Practice CS",
      points: [
        "Real-time data refreshes — no more waiting for manual updates",
        "Saves time and eliminates repetitive work",
        "Frees up resources for higher-value tasks"
      ]
    },
    {
      icon: <Gauge className="w-8 h-8 text-green-600" />,
      title: "Simpler and More Efficient",
      description: "Practice Dashboard isn't a replacement for Practice CS—it's the perfect add-on.",
      points: [
        "Track critical metrics for your firm, staff, and clients—all in one place",
        "More speed, flexibility, and insight than native tools",
        "Seamless integration with existing workflows"
      ]
    },
    {
      icon: <Clock className="w-8 h-8 text-purple-600" />,
      title: "Timesheet Insights",
      description: "Stay informed with real-time updates on work in progress and staff stress levels. Enjoy automatic, always up-to-date data at your fingertips. Make manual timesheet tracking a thing of the past.",
      points: [
        "Real-time work progress tracking",
        "Staff stress level monitoring",
        "Automatic data updates at your fingertips"
      ]
    },
    {
      icon: <AlertTriangle className="w-8 h-8 text-red-600" />,
      title: "Proactive Red Flag Alerts",
      description: "Gain real-time insights into time capturing discipline and Credit Memo issues, enabling swift and informed decisions for C-level users.",
      points: [
        "Real-time insights into time capturing discipline",
        "Credit Memo issue detection",
        "Swift and informed decision-making for C-level users"
      ]
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-indigo-600" />,
      title: "Advanced Reporting",
      description: "Struggling with slow and complicated billing and reporting in Practice CS? Wish your Practice CS reports were more flexible?",
      points: [
        "Pull reports that meet your exact needs",
        "Detailed billing summaries to staff performance metrics",
        "Effortlessly drill down into production analysis by staff and project",
        "Access billing analysis reports in seconds",
        "No hassle of generating and exporting data from Practice CS"
      ]
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      title: "Client Performance Analytics",
      description: "Track client profitability, engagement health, and relationship trends to optimize your firm's growth strategy.",
      points: [
        "Client profitability analysis and trending",
        "Engagement health monitoring and alerts",
        "Revenue per client and growth tracking",
        "Service utilization patterns by client type"
      ]
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Powerful Features for Modern CPA Firms
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Transform your practice management with intelligent automation and real-time insights
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
              <div className="mb-4 sm:mb-6">{feature.icon}</div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-base sm:text-lg">
                {feature.description}
              </p>
              <ul className="space-y-2 sm:space-y-3">
                {feature.points.map((point, pointIndex) => (
                  <li key={pointIndex} className="flex items-start space-x-3">
                    <div className="bg-green-500 rounded-full w-2 h-2 mt-2 flex-shrink-0"></div>
                    <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12 sm:mt-16">
          <a 
            href="/contact" 
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors text-base sm:text-lg min-h-[44px]"
          >
            Get Started Today
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;